import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  HorizonPaymentGatewayModel,
  HorizonPaymentGatewayResponse,
  PaymentSocketData,
  Session,
} from '@shared/components/horizon-payment-getaway/models/horizon-payment-getaway.model';
import { PaymentSocketDataVM } from '@shared/components/horizon-payment-getaway/models/viewmodels/payment-socket-data.viewmodel';
import { PCIPalService } from '@shared/components/horizon-payment-getaway/services/PCI-Pal.service';
import { UrlBuilder } from 'http-url-builder';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HorizonPaymentGatewayService {
  private readonly baseUrl = new UrlBuilder(environment.apim.baseUrl).addPath(
    environment.apim.apiSlugs.paymentGatewayApi
  );
  private paymentStatus$ = new Subject<PaymentSocketData>();
  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient, private pciPalService: PCIPalService) {}

  public closePaymentSession(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pciPalService.closeConnection();
  }

  public initializePaymentSession(
    accessToken: string,
    sessionId: string
  ): void {
    this.pciPalService.openConnection(accessToken, sessionId);

    this.pciPalService
      .getMessages()
      .pipe(
        filter((session) => !!session),
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) =>
            JSON.stringify(this.getRelevantSessionData(prev)) ===
            JSON.stringify(this.getRelevantSessionData(curr))
        )
      )
      .subscribe((session) => {
        this.processPaymentSession(session);
      });
  }

  public GeneratePCIPalSession(
    data: HorizonPaymentGatewayModel
  ): Observable<any> {
    const fullUrl: string = this.baseUrl
      .addPath('payment')
      .addPath('PCIPal')
      .addPath('session')
      .build();
    return this.http.post<HorizonPaymentGatewayResponse>(fullUrl, data);
  }

  public getPaymentStatus(): Observable<PaymentSocketData> {
    return this.paymentStatus$.asObservable();
  }

  public pcipalIframeUrl(sessionId: string): string {
    return this.pciPalService.pcipalIframeUrl(sessionId);
  }

  private getRelevantSessionData(session: Session): any {
    return {
      isComplete: session.IsSessionComplete,
      successCount: session.PaymentDetails.SuccessCount,
      attemptCount: session.PaymentDetails.AttemptCount,
      genesysAttempts: session.VariableData.GenesysAttempts?.Value,
      genesysError: session.VariableData.GenesysCloudErrorMessage?.Value,
    };
  }

  private processPaymentSession(session: Session): void {
    if (
      session.VariableData.GenesysAttempts?.Value === 4 &&
      session.VariableData.GenesysCloudErrorMessage?.Value !== ''
    ) {
      const paymentData = new PaymentSocketDataVM(session, 'warning');
      this.paymentStatus$.next(paymentData);
      return;
    }

    if (
      session.PaymentDetails.AttemptCount > 0 &&
      session.PaymentDetails.SuccessCount === 1
    ) {
      const paymentData = new PaymentSocketDataVM(session, 'success');
      this.paymentStatus$.next(paymentData);
      return;
    }

    if (
      session.IsSessionComplete === true &&
      session.PaymentDetails.SuccessCount === 0
    ) {
      const paymentData = new PaymentSocketDataVM(session, 'error');
      this.paymentStatus$.next(paymentData);
      return;
    }
  }
}
